import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useTranslation } from '@centric-os/helpers';
import type {
  CashlessConfig,
  SitePrivateConfig,
  SitePublicConfig,
  Tender,
} from '@centric-os/types';
import { cloneDeep, isEmpty, isNil } from 'lodash-es';
import { DateTime } from 'luxon';
import ID from '@compassdigital/id';
import { useSiteStore } from '@centric-os/stores/sites/use-site-store';
import { useSiteConfigStore } from '../../../../../../routes/stores';
import { SitesLocalePrefix } from '../../../../../../enums';
import { CASHLESS_PAY_TYPES, CASHLESS_TENDER_TYPES } from '../../../enums';
import { FREEDOM_PAY_PAYMENT_ID } from '../../../../../../constants';

export default () => {
  // Set site configurations store
  const siteConfigStore = useSiteConfigStore();
  const { sitePrivateConfig, sitePublicConfig } = storeToRefs(siteConfigStore);

  // Set localization
  const { translate } = useTranslation();
  const LOCALE_PREFIX = SitesLocalePrefix.SITE_CASHLESS_PAYMENT;

  const initialValues = computed<Partial<CashlessConfig>>(() => {
    const publicConfig = sitePublicConfig.value?.cashless;

    const baseConfig: Partial<CashlessConfig> = {
      id: publicConfig?.id ?? '',
      name: publicConfig?.name ?? '',
      type: publicConfig?.type ?? '',
      voucher_webview: publicConfig?.voucher_webview ?? '',
      webview: publicConfig?.webview ?? '',
      // @ts-expect-error FIXME: Type '""' is not assignable to type 'TenderTypes'.
      tenders: [{ tax_exempt: false, type: '', name: '' }],
    };

    // Set FreedomPay specific config
    if (publicConfig?.type === CASHLESS_PAY_TYPES.FREEDOM_PAY) {
      baseConfig.freedompay = {
        badge_pay_system_key:
          sitePrivateConfig?.value?.payment?.freedompay?.badge_pay_system_key ?? '',
      };
      baseConfig.tenders = mapTenders(publicConfig?.tenders);
    }

    return baseConfig;
  });

  // Helper function to map Tenders
  const mapTenders = (tenders: Partial<Tender>[] | undefined): Tender[] => {
    // @ts-expect-error FIXME: Type '{ id: string; tax_exempt: any; type: any; name: string; max_swipes_per_...
    return (
      tenders?.map((tender) => ({
        id: tender.id,
        // @ts-expect-error FIXME: Property 'tax_exempt' does not exist on type 'Partial<Tender>'.
        tax_exempt: tender.tax_exempt,
        // @ts-expect-error FIXME: Property 'tender_type' does not exist on type 'Partial<Tender>'.
        type: tender.tender_type,
        name: tender.name,
        // @ts-expect-error FIXME: Property 'max_swipes_per_transaction' does not exist on type 'Partial<Tender>'.
        max_swipes_per_transaction: tender.max_swipes_per_transaction,
        // @ts-expect-error FIXME: Property 'rates' does not exist on type 'Partial<Tender>'.
        rates: tender.rates?.map((rate) => ({
          amount: rate.amount / 100,
          startTime: rate?.hours
            ? DateTime.fromFormat(rate.hours.split('-')[0], 'HH:mm').toJSDate()
            : null,
          endTime: rate?.hours
            ? DateTime.fromFormat(rate.hours.split('-')[1], 'HH:mm').toJSDate()
            : null,
        })),
      })) ?? []
    );
  };

  // Computed for cashless pay availability
  const hasCashlessPay = computed<boolean>(() => {
    return !isNil(sitePublicConfig.value?.cashless) && !isEmpty(sitePublicConfig.value?.cashless);
  });

  // Computed for cashless pay types
  const cashlessPayTypes = computed<{ label: string; value: string }[]>(() => {
    const types = [
      {
        label: translate(`${LOCALE_PREFIX}.form.types.freedomPay`),
        value: CASHLESS_PAY_TYPES.FREEDOM_PAY,
      },
    ];

    // Sort types by label alphabetically
    return types.toSorted((a, b) => a.label.localeCompare(b.label));
  });

  // Compute for cashless tenders
  const tenderTypes = computed<{ label: string; value: string }[]>(() => {
    const types = [
      {
        label: translate(`${LOCALE_PREFIX}.form.tenderTypes.badgePay`),
        value: CASHLESS_TENDER_TYPES.BADGE_PAY,
      },
      {
        label: translate(`${LOCALE_PREFIX}.form.tenderTypes.couponVoucher`),
        value: CASHLESS_TENDER_TYPES.COUPON_VOUCHER,
      },
      {
        label: translate(`${LOCALE_PREFIX}.form.tenderTypes.stipend`),
        value: CASHLESS_TENDER_TYPES.STIPEND,
      },
      {
        label: translate(`${LOCALE_PREFIX}.form.tenderTypes.voucher`),
        value: CASHLESS_TENDER_TYPES.VOUCHER,
      },
    ];

    // Sort tenders by label alphabetically
    return types.toSorted((a, b) => a.label.localeCompare(b.label));
  });

  // Map Site Private Config payload
  const mapSitePrivateConfigPayload = (values: Partial<CashlessConfig>): SitePrivateConfig => {
    // Prepare payload
    // Site config
    const siteConfigStore = useSiteConfigStore();
    const { sitePrivateConfig } = storeToRefs(siteConfigStore);

    const payload: SitePrivateConfig = {
      ...sitePrivateConfig.value,
      payment: {
        ...sitePrivateConfig.value?.keystore?.payment,
        freedompay: {
          ...siteConfigStore.value?.payment?.freedompay,
          id: FREEDOM_PAY_PAYMENT_ID,
          badge_pay_system_key: values.freedompay?.badge_pay_system_key,
          freedompay_store_id: 'mobile backwards compatibility',
          freedompay_terminal_id: 'mobile backwards compatibility',
        },
      },
    };

    return cloneDeep(payload);
  };

  // Map Site Public Config payload
  const mapSitePublicConfigPayload = (values: Partial<CashlessConfig>): SitePublicConfig => {
    // Set site store
    const siteStore = useSiteStore();
    const { site } = storeToRefs(siteStore);

    // Prepare payload
    const payload: SitePublicConfig = {
      ...sitePublicConfig.value,
      cashless: {
        id: isEmpty(values.id) ? ID('payment', 'fp', 'cashless', site.value.id) : values.id,
        name: values.name,
        type: values.type,
        voucher_webview: values.voucher_webview,
        webview: values.webview,
        // @ts-expect-error FIXME: Argument of type '(tender: Tender) => { tax_exempt: any; tender_type: TenderT...
        tenders: values.tenders.map((tender: Tender) => ({
          // @ts-expect-error FIXME: Property 'tax_exempt' does not exist on type 'Tender'.
          tax_exempt: tender.tax_exempt,
          tender_type: tender.type,
          name: tender.name,
        })),
      },
      badge_pay: {
        id: isEmpty(sitePublicConfig.value?.badge_pay?.id)
          ? ID('payment', 'fp', 'badgepay', site.value.id)
          : sitePublicConfig.value?.badge_pay?.id,
        name: values.name,
        type: values.type,
        webview: values.webview,
      },
    };

    return cloneDeep(payload);
  };

  // Map Delete Private Config payload
  const mapDeletePrivateConfigPayload = (): SitePrivateConfig => {
    const payload: SitePrivateConfig = cloneDeep(sitePrivateConfig.value);
    delete payload.payment.freedompay.badge_pay_system_key;
    delete payload.payment.freedompay.freedompay_store_id;
    delete payload.payment.freedompay.freedompay_terminal_id;

    return cloneDeep(payload);
  };

  // Map Delete Public Config payload
  const mapDeletePublicConfigPayload = (): SitePublicConfig => {
    // Prepare payload
    const payload: SitePublicConfig = cloneDeep(sitePublicConfig.value);

    delete payload.cashless;
    delete payload.badge_pay;

    return cloneDeep(payload);
  };

  return {
    initialValues,
    hasCashlessPay,
    cashlessPayTypes,
    tenderTypes,
    mapSitePrivateConfigPayload,
    mapSitePublicConfigPayload,
    mapDeletePrivateConfigPayload,
    mapDeletePublicConfigPayload,
  };
};
